/**
 * Buffer for the Chromium `beforeinstallprompt` event.
 *
 * That event fires once, very early — usually before React has mounted — and
 * is only usable if `preventDefault()` was called on it synchronously. A
 * React effect is therefore too late to catch it, so capture is registered at
 * module import time (from `main.tsx`) and the event is parked here until a
 * component subscribes.
 *
 * The saved event is single-use: once `prompt()` has been called on it the
 * browser will not let it be reused, so consumers clear it afterwards.
 */

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

type Listener = (event: BeforeInstallPromptEvent | null) => void

let deferredEvent: BeforeInstallPromptEvent | null = null
const listeners = new Set<Listener>()

function notify() {
  listeners.forEach((listener) => listener(deferredEvent))
}

/** The parked event, or `null` when the browser has not offered one. */
export function getDeferredInstallEvent(): BeforeInstallPromptEvent | null {
  return deferredEvent
}

/** Drops the parked event — after it has been consumed, or once installed. */
export function clearDeferredInstallEvent(): void {
  deferredEvent = null
  notify()
}

/**
 * Subscribes to arrivals/removals of the parked event. Returns the
 * unsubscribe function.
 */
export function subscribeToInstallPrompt(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function isBeforeInstallPromptEvent(event: Event): event is BeforeInstallPromptEvent {
  return 'prompt' in event && 'userChoice' in event
}

/**
 * Registers the window-level capture. Called once from `main.tsx`; returns a
 * teardown function so tests (and a future hot-reload) can unregister it.
 */
export function captureBeforeInstallPrompt(browserWindow: Window = window): () => void {
  const onBeforeInstallPrompt = (event: Event) => {
    if (!isBeforeInstallPromptEvent(event)) return

    // Suppresses Chromium's own mini-infobar so the invitation below is the
    // only install affordance on screen.
    event.preventDefault()
    deferredEvent = event
    notify()
  }

  const onAppInstalled = () => clearDeferredInstallEvent()

  browserWindow.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
  browserWindow.addEventListener('appinstalled', onAppInstalled)

  return () => {
    browserWindow.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    browserWindow.removeEventListener('appinstalled', onAppInstalled)
  }
}
